import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import confetti from "canvas-confetti";
import { RelayLoader } from "../components/RelayLoader";
import { ConfirmNotice } from "../components/ConfirmNotice";
import { homeSection } from '../lib/marketingHome';
import { mask, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
};

const fmt = (n) => (n == null ? "n/a" : mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`));
const fullDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");

const FOCUSES = ["Keep my pace", "More breathing room", "Raise my target", "Rebuild the habit"];
const TAX_DATES = ["January 15", "April 15", "June 15", "September 15"];

// Given a quarter key like "2026-Q3", return the human-readable start date of
// the NEXT IRS quarter (Q1 → Apr 1, Q2 → Jun 1, Q3 → Sep 1, Q4 → Jan 1 next yr).
const nextQuarterStartLabel = (qkey) => {
  if (!qkey) return "the next quarter";
  const m = /^(\d{4})-Q([1-4])$/.exec(qkey);
  if (!m) return "the next quarter";
  const year = parseInt(m[1], 10);
  const q = parseInt(m[2], 10);
  const map = {
    1: { label: "April 1", quarter: "Q2", year },
    2: { label: "June 1", quarter: "Q3", year },
    3: { label: "September 1", quarter: "Q4", year },
    4: { label: `January 1, ${year + 1}`, quarter: "Q1", year: year + 1 },
  };
  const nxt = map[q];
  return `${nxt.label} (${nxt.quarter})`;
};

const FEEL_OPTIONS = [
  { word: "Smooth", hint: "I found a rhythm and mostly stayed with it." },
  { word: "Steady", hint: "It took effort, but the pace worked." },
  { word: "Tight", hint: "I made it work, but there wasn't much room." },
  { word: "Crunchy", hint: "The numbers or timing felt harder than expected." },
  { word: "Chaotic", hint: "Life/work kept shifting under me." },
  { word: "Heavy", hint: "The target felt like too much to carry." },
  { word: "Too easy", hint: "I may have room to raise my target or goals." },
  { word: "Uneven", hint: "Some weeks worked, some weeks didn't." },
  { word: "Surprising", hint: "I learned something I didn't expect." },
  { word: "Off track", hint: "The plan stopped matching real life." },
];

const Chip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-4 py-2 rounded-full text-sm font-semibold border transition-transform hover:scale-105"
    style={{
      background: active ? COLOR.ink : COLOR.card,
      color: active ? "#fff" : COLOR.ink2,
      borderColor: active ? COLOR.ink : COLOR.line,
    }}
    data-testid={testId}
  >
    {label}
  </button>
);

export default function QuarterlyResetPage() {
  usePrivacy();  // re-render when Creator Privacy Mode flips
  const navigate = useNavigate();
  const [view, setView] = useState("loading"); // loading | not_member | flow
  const [quarter, setQuarter] = useState("");
  const [history, setHistory] = useState([]);
  const DRAFT_KEY = "pr.quarterly.draft";
  const draft = (() => { try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; } })();
  const qs = new URLSearchParams(window.location.search);
  const fromCalculator = qs.get("from") === "calculator";
  const calcTarget = fromCalculator && parseFloat(qs.get("target")) > 0 ? String(Math.round(parseFloat(qs.get("target")))) : "";
  const [step, setStep] = useState(fromCalculator ? 2 : 0);
  const [feels, setFeels] = useState(draft.feels || []);
  const feel = feels.join(", ");
  const toggleFeel = (f) => setFeels((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  const [wins, setWins] = useState(draft.wins || "");
  const [pitfalls, setPitfalls] = useState(draft.pitfalls || "");
  const [taxAck, setTaxAck] = useState(!!draft.taxAck);
  const [focus, setFocus] = useState(draft.focus || "");
  const [adjustments, setAdjustments] = useState(draft.adjustments || "");
  const [newTarget, setNewTarget] = useState(calcTarget || draft.newTarget || "");

  // Keep the in-progress answers while the racer hops out to the Relay Calculator and back.
  useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ feels, wins, pitfalls, taxAck, focus, adjustments, newTarget })); } catch { /* noop */ }
  }, [feels, wins, pitfalls, taxAck, focus, adjustments, newTarget]);
  useEffect(() => {
    if (calcTarget) toast.success(`Relay Calculator says ${fmt(parseFloat(calcTarget))}/week, review and lock it in below.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sheetUpdated, setSheetUpdated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);
  const [goalApplied, setGoalApplied] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/caddy/summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.calendar) setCalendar(d.calendar); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/quarterly/history`, { credentials: "include" });
        if (res.status === 401) { navigate("/login?next=/app/reset", { replace: true }); return; }
        if (res.status === 403) { setView("not_member"); return; }
        const data = await res.json();
        setQuarter(data.quarter);
        setHistory(data.resets || []);
        setView("flow");
      } catch {
        setView("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSave = async (applyImpact) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/caddy/quarterly`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          feel, wins, pitfalls, tax_ack: taxAck, focus, adjustments,
          ...(newTarget ? { new_weekly_target: parseFloat(newTarget) } : {}),
          apply_goal_impact: Boolean(applyImpact),
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't save, try again"); return; }
      setImpact(null);
      setGoalApplied(data.goal_impact || null);
      setSheetUpdated(Boolean(data.sheet_updated));
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      setStep(3);
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ["#F5CB5C", "#3E8E4E", "#2A1E5C"] });
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!feel || !focus || saving) return;
    if (newTarget && parseFloat(newTarget) > 0) {
      setSaving(true);
      try {
        const res = await fetch(`${API}/api/caddy/quarterly/impact`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ new_weekly_target: parseFloat(newTarget) }),
        });
        const data = await res.json();
        if (res.ok && data.applicable) { setSaving(false); setImpact(data); return; }
      } catch { /* fall through to a normal save */ }
      setSaving(false);
    }
    doSave(false);
  };

  const handleLockInClick = () => {
    if (!feel || !focus || saving) return;
    setConfirmOpen(true);
  };

  const confirmAndSave = () => {
    setConfirmOpen(false);
    save();
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }}>
        <RelayLoader label="Loading your quarterly tax day…" />
      </div>
    );
  }

  if (view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="qr-join-gate">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Quarterly Resets ride with Payday Relay
          </h1>
          <Link to={homeSection('season-pass')} className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-ink-surface)", color: "#fff" }}>
            See membership options →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pr-bg)" }}>
      {confirmOpen && (
        <ConfirmNotice
          testId="qr-confirm"
          eyebrow="Before you lock this in"
          title={`Are you sure you want to lock this in for ${nextQuarterStartLabel(quarter)}?`}
          lead="This is the real thing, not a scenario. Once it is locked, your pace is set until your next quarterly tax day."
          items={[
            "Your weekly target for the rest of the race",
            "Your progress bar and the weeks you have already raced, they get measured against the new number",
            "The goal numbers written into your Google Sheet",
          ]}
          confirmLabel="Yes, lock it in"
          cancelLabel="Go back"
          busy={saving}
          onConfirm={confirmAndSave}
          onCancel={() => setConfirmOpen(false)}
        >
          <div className="p-4 mb-5" style={{ background: "#FFFFFF", border: "1px solid #E8E2D5" }}>
            <p className="font-body text-xs mb-1" style={{ color: "#5D5A6B" }}>
              <b style={{ color: "#2A1E5C" }}>Focus:</b> {focus || "Not set"}
            </p>
            <p className="font-body text-xs" style={{ color: "#5D5A6B" }} data-testid="qr-confirm-next-quarter">
              <b style={{ color: "#2A1E5C" }}>Starts:</b> {nextQuarterStartLabel(quarter)}
            </p>
            {newTarget && parseFloat(newTarget) > 0 && (
              <p className="font-body text-xs mt-1" style={{ color: "#5D5A6B" }} data-testid="qr-confirm-new-target">
                <b style={{ color: "#2A1E5C" }}>New weekly target:</b> {fmt(parseFloat(newTarget))}
              </p>
            )}
          </div>
        </ConfirmNotice>
      )}
      {impact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5" style={{ background: "rgba(26,16,60,0.72)" }} data-testid="sanity-check-modal">
          <div className="w-full max-w-md rounded-3xl p-6 max-h-[85vh] overflow-y-auto" style={{ background: "var(--pr-card)" }}>
            <h2 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Year-end sanity check
            </h2>
            <p className="text-sm leading-relaxed mb-3" style={{ color: COLOR.ink2 }}>
              Easing your pace from <b style={{ color: COLOR.ink }}>{fmt(impact.current_target)}</b> to{" "}
              <b style={{ color: COLOR.ink }}>{fmt(impact.new_target)}</b> a week means about{" "}
              <b style={{ color: "var(--pr-red)" }} data-testid="sanity-shortfall">{fmt(impact.shortfall)}</b> less crossing
              the finish line over your remaining ~{impact.remaining_weeks} working weeks.
            </p>
            {(impact.debt || impact.savings) ? (
              <div className="rounded-xl p-3 mb-3 space-y-1.5" style={{ background: "var(--pr-bg)" }}>
                {impact.debt && (
                  <p className="text-sm" style={{ color: COLOR.ink2 }} data-testid="sanity-debt-row">
                    💳 Debt payoff goal: <s>{fmt(impact.debt.current)}</s> → <b style={{ color: COLOR.ink }}>{fmt(impact.debt.projected)}</b>
                  </p>
                )}
                {impact.savings && (
                  <p className="text-sm" style={{ color: COLOR.ink2 }} data-testid="sanity-savings-row">
                    🎯 Savings goal: <s>{fmt(impact.savings.current)}</s> → <b style={{ color: COLOR.ink }}>{fmt(impact.savings.projected)}</b>
                  </p>
                )}
                {impact.spillover > 0 && (
                  <p className="text-xs" style={{ color: COLOR.muted }}>
                    The remaining ~{fmt(impact.spillover)} comes out of weekly take-home.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm mb-3" style={{ color: COLOR.ink2 }}>
                No debt or savings goals on file. Your weekly take-home absorbs the difference.
              </p>
            )}
            <p className="text-xs leading-relaxed mb-4" style={{ color: COLOR.muted }}>
              No judgment, a pace you can actually hold beats a goal that breaks you. Confirm and we'll adjust your
              goals and your sheet so the finish line stays honest.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setImpact(null)}
                className="flex-1 py-3 rounded-full font-bold text-sm border"
                style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "var(--pr-card)" }}
                data-testid="sanity-back-btn"
              >
                Go back
              </button>
              <button
                onClick={() => doSave(true)}
                disabled={saving}
                className="flex-1 py-3 rounded-full font-bold text-sm disabled:opacity-40"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="sanity-confirm-btn"
              >
                {saving ? "Saving…" : "Adjust & lock in →"}
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="px-5 pt-5 pb-2">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="qr-back-link">
            <ArrowLeft className="w-4 h-4" /> Payday Relay
          </Link>
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: COLOR.muted }}>🛠️ Quarterly Reset · {quarter}</p>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <div className="max-w-md mx-auto">
          {/* Race settings, moved off the daily logging screen */}
          {calendar?.start_date && (
            <div className="rounded-2xl p-5 lg:p-6 pr-card mb-6" data-testid="qr-race-settings">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: COLOR.muted }}>
                Your settings
              </p>
              <dl className="text-sm space-y-1.5">
                <div className="flex justify-between gap-3">
                  <dt style={{ color: COLOR.ink2 }}>You started</dt>
                  <dd className="font-semibold" style={{ color: COLOR.ink }} data-testid="qr-setting-start">{fullDate(calendar.start_date)}</dd>
                </div>
                {calendar.payday_delay != null && (
                  <div className="flex justify-between gap-3">
                    <dt style={{ color: COLOR.ink2 }}>Payday rhythm</dt>
                    <dd className="font-semibold" style={{ color: COLOR.ink }} data-testid="qr-setting-rhythm">{calendar.payday_delay} day{calendar.payday_delay === 1 ? "" : "s"}</dd>
                  </div>
                )}
                {calendar.days_per_week && (
                  <div className="flex justify-between gap-3">
                    <dt style={{ color: COLOR.ink2 }}>Work days per week</dt>
                    <dd className="font-semibold" style={{ color: COLOR.ink }} data-testid="qr-setting-days">{calendar.days_per_week}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Progress dots */}
          {step < 3 && (
            <div className="flex justify-center gap-2 mb-6">
              {[0, 1, 2].map((i) => (
                <span key={i} className="w-2 h-2 rounded-full" style={{ background: i <= step ? COLOR.ink : COLOR.line }} />
              ))}
            </div>
          )}

          {step === 0 && (
            <div className="space-y-5" data-testid="qr-step-reflect">
              <div className="text-center">
                <h1 className="text-3xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  How did last quarter feel?
                </h1>
                <p className="text-sm" style={{ color: COLOR.ink2 }}>
                  No dollar-amount judgments here. This is about the rhythm, not the total.
                </p>
              </div>
              <p className="text-xs text-center -mt-2" style={{ color: COLOR.muted }}>Check every word that fits, as many as you like.</p>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="qr-feel-options">
                {FEEL_OPTIONS.map(({ word, hint }) => {
                  const on = feels.includes(word);
                  return (
                    <label
                      key={word}
                      className="flex items-start gap-3 rounded-xl px-4 py-3 cursor-pointer border transition-colors"
                      style={{ background: on ? "var(--pr-tint-ink)" : COLOR.card, borderColor: on ? COLOR.ink : COLOR.line }}
                      data-testid={`qr-feel-${word.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <input type="checkbox" checked={on} onChange={() => toggleFeel(word)} className="mt-1 accent-[#2A1E5C]" data-testid={`qr-feel-check-${word.toLowerCase().replace(/\s+/g, "-")}`} />
                      <span>
                        <span className="block text-sm font-bold" style={{ color: COLOR.ink }}>{word}</span>
                        <span className="block text-xs leading-relaxed" style={{ color: COLOR.ink2 }}>{hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Wins worth keeping 🏆</label>
                <textarea
                  value={wins}
                  onChange={(e) => setWins(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Habits that stuck, weeks that felt good, anything you're proud of…"
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="qr-wins-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Pitfalls to name (not blame)</label>
                <textarea
                  value={pitfalls}
                  onChange={(e) => setPitfalls(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Where did the relay feel too tight? What kept tripping you up?"
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="qr-pitfalls-input"
                />
              </div>
              <button
                onClick={() => setStep(1)}
                disabled={!feel}
                className="w-full py-3.5 rounded-full font-bold text-base disabled:opacity-40 transition-transform hover:scale-[1.02]"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="qr-next-btn"
              >
                Next: tax check-in →
              </button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="qr-step-tax">
              <div className="text-center">
                <h1 className="text-3xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  Tax check-in 🧾
                </h1>
                <p className="text-sm" style={{ color: COLOR.ink2 }}>
                  A friendly nudge, not advice: self-employed folks typically make estimated tax payments four times a year.
                </p>
              </div>
              <div className="rounded-2xl p-5 lg:p-6 pr-card">
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: COLOR.muted }}>IRS estimated tax due dates</p>
                <div className="grid grid-cols-2 gap-2">
                  {TAX_DATES.map((d) => (
                    <div key={d} className="text-sm font-semibold px-3 py-2 rounded-lg text-center" style={{ background: "var(--pr-bg)", color: COLOR.ink }}>
                      {d}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] mt-3 leading-relaxed" style={{ color: COLOR.muted }}>
                  Your sheet's SCOREBOARD tracks what you've set aside. This is educational only, for your actual
                  amounts and filing, talk to a CPA.
                </p>
              </div>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer" style={{ color: COLOR.ink }}>
                <input
                  type="checkbox"
                  checked={taxAck}
                  onChange={(e) => setTaxAck(e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  data-testid="qr-tax-ack"
                />
                I've checked my tax set-asides for the quarter (or made a plan to)
              </label>
              <button
                onClick={() => setStep(2)}
                className="w-full py-3.5 rounded-full font-bold text-base transition-transform hover:scale-[1.02]"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="qr-next-btn-2"
              >
                Next: the quarter ahead →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="qr-step-ahead">
              <button type="button" onClick={() => setStep(1)} className="text-xs font-semibold underline" style={{ color: COLOR.ink2 }} data-testid="qr-back-to-tax">
                ← Back
              </button>
              <div className="text-center">
                <h1 className="text-3xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  The quarter ahead
                </h1>
                <p className="text-sm" style={{ color: COLOR.ink2 }}>
                  Adjust your pace within the structure, don't rewrite the whole race.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {FOCUSES.map((f) => (
                  <Chip key={f} label={f} active={focus === f} onClick={() => setFocus(f)} testId={`qr-focus-${f.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>Adjustments you're planning</label>
                <textarea
                  value={adjustments}
                  onChange={(e) => setAdjustments(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="More work days? A gentler target? A new logging routine?"
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="qr-adjustments-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: COLOR.ink }}>New weekly target for the quarter ahead (optional)</label>
                <input
                  type="number" inputMode="decimal" min="1" step="0.01"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  placeholder="Leave blank to keep your current pace"
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="qr-new-target-input"
                />
                <p className="text-[11px] mt-1" style={{ color: COLOR.muted }}>
                  Set one and we write it straight into your Google Sheet for the remaining quarters.
                </p>
              </div>
              <Link
                to="/app/calculator?from=pit-stop"
                className="block w-full py-3 rounded-full font-bold text-sm text-center border-2 transition-transform hover:scale-[1.02]"
                style={{ borderColor: COLOR.ink, color: COLOR.ink }}
                data-testid="qr-rerun-calculator"
              >
                {calcTarget ? "Re-run the Relay Calculator ↻" : "Re-run the Relay Calculator to find your new pace →"}
              </Link>
              {calcTarget && (
                <p className="text-xs text-center font-semibold rounded-xl px-3 py-2" style={{ color: COLOR.ink, background: "var(--pr-tint-green)" }} data-testid="qr-calc-target-note">
                  ✓ New target pulled from your Relay Calculator run. Your answers from the earlier steps are saved.
                </p>
              )}
              <button
                onClick={handleLockInClick}
                disabled={!focus || saving}
                className="w-full py-3.5 rounded-full font-bold text-base disabled:opacity-40 transition-transform hover:scale-[1.02]"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="qr-save-btn"
              >
                {saving ? "Saving…" : "Lock in my reset 🏁"}
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="text-center space-y-5" data-testid="qr-done">
              <h1 className="text-3xl" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                Reset locked in
              </h1>
              <p className="text-sm" style={{ color: COLOR.ink2 }}>
                {quarter} is set. Fresh quarter, same race, adjusted pace.
              </p>
              {sheetUpdated && (
                <p className="text-sm font-bold" style={{ color: "var(--pr-green)" }} data-testid="qr-sheet-updated">
                  ✓ New pace written into your Google Sheet
                </p>
              )}
              {goalApplied && (
                <div className="text-sm rounded-xl p-3 inline-block text-left" style={{ background: "var(--pr-tint-green)", color: "var(--pr-green)" }} data-testid="qr-goal-adjusted">
                  <p className="font-bold">✓ Year-end goals adjusted to your new pace</p>
                  {goalApplied.debt && <p className="text-xs mt-0.5">Debt payoff goal: {fmt(goalApplied.debt.projected)}</p>}
                  {goalApplied.savings && <p className="text-xs mt-0.5">Savings goal: {fmt(goalApplied.savings.projected)}</p>}
                </div>
              )}
              <Link
                to="/app"
                className="inline-block px-8 py-3.5 rounded-full font-bold transition-transform hover:scale-105"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="qr-back-to-caddy"
              >
                Back to Payday Relay →
              </Link>
            </div>
          )}

          {/* Past resets */}
          {step !== 3 && history.length > 0 && (
            <div className="mt-10" data-testid="qr-history">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: COLOR.muted }}>Past resets</p>
              <div className="space-y-2">
                {history.map((r) => (
                  <div key={r.quarter} className="rounded-xl p-4 border text-sm" style={{ background: "var(--pr-card)", borderColor: COLOR.line }}>
                    <p className="font-bold" style={{ color: COLOR.ink }}>{r.quarter} · felt {r.feel?.toLowerCase()} · focus: {r.focus}</p>
                    {r.wins && <p className="text-xs mt-1" style={{ color: COLOR.ink2 }}>🏆 {r.wins}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-center leading-relaxed mt-10" style={{ color: COLOR.muted }} data-testid="qr-disclaimer">
            Payday Relay is an educational tool. Not financial, tax, or investment advice. For specifics, talk to a CPA.
          </p>
        </div>
      </main>
    </div>
  );
}
